import { html, cx, useState, useEffect, useRef } from '../h.js'
import { Icon } from '../icons.js'
import { t } from '../i18n.js'
import { copyText, shortId, listTime } from '../format.js'
import {
  Modal, ConfirmDialog, CopyField, Avatar, Badges, useToast, Empty, Switch, SettingRow, errorText
} from '../components.js'
import { useApp } from '../store.js'
import { StoryComposer } from './stories.js'
import { ROOM_TYPE, ACTION, normalizeUsername, isValidUsername, formatUsername, REPORT_REASONS, Invite, INVITE_KIND } from '@pine/core'

/** One switchboard so every screen can raise a dialog through the store. */
export function Dialogs () {
  const store = useApp()
  const dialog = store.get('dialog')
  if (!dialog) return null

  const close = () => store.hideDialog()

  switch (dialog.type) {
    case 'new': return html`<${NewChatDialog} onClose=${close} />`
    case 'group': return html`<${CreateRoomDialog} type=${ROOM_TYPE.GROUP} onClose=${close} />`
    case 'channel': return html`<${CreateRoomDialog} type=${ROOM_TYPE.CHANNEL} onClose=${close} />`
    case 'join': return html`<${JoinDialog} onClose=${close} prefill=${dialog.code} />`
    case 'collect': return html`<${CollectDialog} onClose=${close} token=${dialog.token} />`
    case 'add-to-group': return html`<${AddToGroupDialog} onClose=${close} account=${dialog.account} name=${dialog.name} />`
    case 'story-new': return html`<${StoryComposer} onClose=${close} item=${dialog.item} />`
    case 'direct': return html`<${DirectDialog} onClose=${close} peer=${dialog.peer} name=${dialog.name} />`
    case 'invite': return html`<${InviteDialog} roomId=${dialog.roomId} onClose=${close} />`
    case 'search': return html`<${SearchDialog} roomId=${dialog.roomId} onClose=${close} />`
    case 'forward': return html`<${ForwardDialog} dialog=${dialog} onClose=${close} />`
    case 'leave': return html`<${LeaveDialog} roomId=${dialog.roomId} onClose=${close} />`
    case 'delete-message': return html`<${DeleteMessageDialog} dialog=${dialog} onClose=${close} />`
    case 'signout': return html`<${SignOutDialog} onClose=${close} />`
    case 'contact-dev': return html`<${ContactDevDialog} dialog=${dialog} onClose=${close} />`
    case 'link-device': return html`<${LinkDeviceDialog} onClose=${close} />`
    case 'poll': return html`<${PollDialog} roomId=${dialog.roomId} onClose=${close} />`
    case 'report': return html`<${ReportDialog} dialog=${dialog} onClose=${close} />`
    default: return null
  }
}

/* ------------------------------------------------------------------ */

/**
 * Add another device to this account.
 *
 * The other device shows a code; this one reads it and sends the account over.
 * Sending is the dangerous direction — whoever holds that code ends up holding
 * the account — so it asks first, in as many words, and never sends on a scan
 * alone.
 *
 * The camera is used where the platform has a barcode reader, which in
 * practice means the phone. Everywhere else the code is pasted, which is why
 * the other device shows it as text underneath the square.
 */
function LinkDeviceDialog ({ onClose }) {
  const store = useApp()
  const toast = useToast()
  const videoRef = useRef(null)

  const [code, setCode] = useState('')
  const [pending, setPending] = useState(null)
  const [busy, setBusy] = useState(false)
  const [scanning, setScanning] = useState(false)

  // Scan, where there is something to scan with. Chromium on Android has a
  // barcode reader built in; desktop Chromium mostly does not, and there the
  // field below is the whole interface.
  useEffect(() => {
    if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return

    let stream = null
    let stopped = false
    setScanning(true)

    ;(async () => {
      try {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        })
        if (stopped) return
        const video = videoRef.current
        if (!video) return
        video.srcObject = stream
        await video.play().catch(() => {})

        const tick = async () => {
          if (stopped) return
          try {
            const found = await detector.detect(video)
            const hit = found.find((b) => String(b.rawValue || '').startsWith('pine-link/'))
            if (hit) {
              setPending(hit.rawValue)
              setCode(hit.rawValue)
              return
            }
          } catch { /* a frame that could not be read is not an error */ }
          setTimeout(tick, 400)
        }
        tick()
      } catch {
        setScanning(false) // no camera, or permission refused
      }
    })()

    return () => {
      stopped = true
      if (stream) for (const track of stream.getTracks()) track.stop()
    }
  }, [])

  async function send (value) {
    setBusy(true)
    try {
      await store.client.linkDevice(value)
      toast(t('devices.linked'))
      onClose()
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
      setPending(null)
    } finally {
      setBusy(false)
    }
  }

  if (pending) {
    return html`
      <${ConfirmDialog}
        title=${t('devices.confirmTitle')}
        message=${t('devices.confirmBody')}
        confirmLabel=${busy ? t('devices.linking') : t('devices.confirmAction')}
        danger
        onConfirm=${() => { if (!busy) send(pending) }}
        onCancel=${() => setPending(null)} />`
  }

  return html`
    <${Modal} title=${t('devices.add')} onClose=${onClose}>
      <p class="lede" style="font-size:var(--text-md)">${t('devices.scanLede')}</p>

      ${scanning && html`
        <div class="scanner">
          <video ref=${videoRef} playsinline muted></video>
          <div class="scanner-frame"></div>
        </div>`}

      <div class="field" style="margin-top:12px">
        <label>${t('devices.pasteLabel')}</label>
        <input class="input mono" value=${code} spellcheck="false"
               placeholder="pine-link/1:…"
               onInput=${(e) => setCode(e.target.value)} />
      </div>

      <button class="btn btn-primary btn-block" disabled=${busy || !code.trim().startsWith('pine-link/')}
              onClick=${() => setPending(code.trim())}>
        ${t('devices.addAction')}
      </button>
    </${Modal}>`
}

function NewChatDialog ({ onClose }) {
  const store = useApp()
  const options = [
    { id: 'group', icon: Icon.users(22), title: t('new.group'), sub: t('new.groupHint') },
    { id: 'channel', icon: Icon.channel(22), title: t('new.channel'), sub: t('new.channelHint') },
    { id: 'direct', icon: Icon.userPlus(22), title: t('new.direct'), sub: t('new.directHint') },
    { id: 'join', icon: Icon.link(22), title: t('new.join'), sub: t('new.joinHint') }
  ]

  return html`
    <${Modal} title=${t('new.title')} onClose=${onClose}>
      <div class="col gap2" style="margin:-8px -8px">
        ${options.map((option) => html`
          <button key=${option.id} class="list-row" style="border-radius:var(--r-md)"
                  onClick=${() => store.showDialog({ type: option.id })}>
            <span class="glyph" style="color:var(--accent)">${option.icon}</span>
            <span class="body">
              <span class="title strong">${option.title}</span>
              <span class="sub">${option.sub}</span>
            </span>
            ${Icon.forward(16)}
          </button>`)}
      </div>
    <//>`
}

function CreateRoomDialog ({ type, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => { if (inputRef.current) inputRef.current.focus() }, [])

  async function create () {
    if (!name.trim()) return
    setBusy(true)
    try {
      const room = await store.client.createRoom({
        name: name.trim(),
        description: description.trim(),
        type
      })
      onClose()
      await store.openRoom(room.id)
    } catch (err) {
      toast(errorText(err), { error: true })
    } finally {
      setBusy(false)
    }
  }

  const title = type === ROOM_TYPE.CHANNEL ? t('new.channel') : t('new.group')

  return html`
    <${Modal} title=${title} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        <button class="btn btn-primary" disabled=${busy || !name.trim()} onClick=${create}>
          ${busy ? html`<div class="spinner"></div>` : t('new.create')}
        </button>`}>
      <div class="field">
        <label>${t('new.name')}</label>
        <input ref=${inputRef} class="input" value=${name} maxlength="128"
               placeholder=${t('new.namePlaceholder')}
               onInput=${(e) => setName(e.target.value)}
               onKeyDown=${(e) => { if (e.key === 'Enter') create() }} />
      </div>
      <div class="field">
        <label>${t('new.description')}</label>
        <input class="input" value=${description} maxlength="1024"
               onInput=${(e) => setDescription(e.target.value)} />
      </div>
    <//>`
}

function JoinDialog ({ onClose, prefill = '' }) {
  const store = useApp()
  const toast = useToast()
  const [code, setCode] = useState(prefill || '')
  // Opened from a link: say where it leads instead of showing the code.
  const preview = prefill ? invitePreview(prefill) : null
  const [busy, setBusy] = useState(false)
  const [stage, setStage] = useState('')

  async function join () {
    setBusy(true)
    setStage(t('call.connecting'))
    try {
      const room = await store.client.joinRoom(code.trim(), {
        onProgress: (phase) => setStage(phase === 'searching' ? t('call.connecting') : t('action.done'))
      })
      onClose()
      await store.openRoom(room.id)
    } catch (err) {
      toast(errorText(err, 'err.joinTimeout'), { error: true })
    } finally {
      setBusy(false)
      setStage('')
    }
  }

  return html`
    <${Modal} title=${t('new.join')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        <button class="btn btn-primary" disabled=${busy || code.trim().length < 8} onClick=${join}>
          ${busy ? html`<div class="spinner"></div>` : t('new.join')}
        </button>`}>
      ${preview
        ? html`
          <div class="invite-card">
            <${Avatar} name=${preview.name} id=${code} size="lg" square=${preview.channel} />
            <div class="grow">
              <div class="strong">${preview.name || t('invite.unnamed')}</div>
              <div class="small dim">${preview.channel ? t('invite.channel') : t('invite.group')}</div>
            </div>
          </div>
          ${busy && html`<span class="hint">${stage}</span>`}`
        : html`
          <div class="field">
            <label>${t('panel.inviteLink')}</label>
            <textarea class="textarea mono" style="min-height:70px" value=${code}
                      placeholder=${t('new.invitePlaceholder')}
                      onInput=${(e) => setCode(e.target.value)}></textarea>
            ${busy && html`<span class="hint">${stage}</span>`}
          </div>`}
    <//>`
}

/**
 * What an invite link says about where it leads, before anybody is asked.
 *
 * The name travels inside the link itself — the inviter's device put it there
 * — so this needs no network and no server. It is a hint, not a promise: the
 * room's own name is only known once inside it.
 */
export function invitePreview (code) {
  try {
    const invite = Invite.parse(code)
    if (invite.kind === INVITE_KIND.CONTACT) return null
    return { name: invite.hint || '', channel: invite.kind === INVITE_KIND.CHANNEL }
  } catch {
    return null
  }
}

/**
 * Take a collectible name from the link it came in.
 *
 * Collectible names are issued by the operator one at a time, each with a link
 * that binds it to whichever account opens it first and says yes. Asked, not
 * done on sight: it replaces the name this account has now.
 */
function CollectDialog ({ onClose, token }) {
  const store = useApp()
  const toast = useToast()
  const [info, setInfo] = useState(null)
  const [state, setState] = useState('loading') // loading | ready | gone | offline
  const [busy, setBusy] = useState(false)
  const current = store.client.username

  useEffect(() => {
    let alive = true
    store.client.registry.describeCollectible(token).then((found) => {
      if (!alive) return
      if (!found) return setState('offline')
      setInfo(found)
      setState(found.redeemed ? 'gone' : 'ready')
    })
    return () => { alive = false }
  }, [token])

  async function take () {
    setBusy(true)
    try {
      const username = await store.client.collectUsername(token)
      toast(t('collect.done', { name: formatUsername(username) }))
      onClose()
    } catch (err) {
      const reason = err.reason || 'failed'
      toast(t('collect.err.' + (['used', 'unknown', 'taken', 'frozen', 'offline'].includes(reason) ? reason : 'failed')), { error: true })
      if (reason === 'used') setState('gone')
    } finally {
      setBusy(false)
    }
  }

  return html`
    <${Modal} title=${t('collect.title')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        ${state === 'ready' && html`
          <button class="btn btn-primary" disabled=${busy} onClick=${take}>
            ${busy ? html`<div class="spinner"></div>` : t('collect.take')}
          </button>`}`}>
      ${state === 'loading' && html`<div class="row center" style="padding:24px"><div class="spinner lg"></div></div>`}
      ${state === 'offline' && html`<p class="dim">${t('collect.err.unknown')}</p>`}
      ${info && html`
        <div class="collect-card">
          <div class="collect-name">${formatUsername(info.username)}</div>
          <div class="small dim">${t('collect.kind')}</div>
        </div>`}
      ${state === 'gone' && html`<p class="dim">${t('collect.err.used')}</p>`}
      ${state === 'ready' && html`
        <p class="small">${t('collect.explain')}</p>
        ${current && current !== (info && info.username) && html`
          <p class="small" style="color:var(--warning)">${t('collect.replaces', { name: formatUsername(current) })}</p>`}`}
    <//>`
}

/**
 * Start a one-to-one chat.
 *
 * Two ways in: paste a Pine ID, or search a handle. The handle path resolves
 * over the DHT and can legitimately return more than one person, because
 * nothing in a serverless design makes a name exclusive — so results are a
 * list with account ids attached, not a single silent answer.
 */
function DirectDialog ({ onClose, peer = '', name = '' }) {
  const store = useApp()
  const toast = useToast()

  const [query, setQuery] = useState(peer || '')
  const [results, setResults] = useState(null)
  const [selected, setSelected] = useState(peer ? { account: peer, name } : null)
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => { if (inputRef.current && !peer) inputRef.current.focus() }, [])

  const trimmed = query.trim()
  const looksLikeId = /^[a-z0-9]{45,}$/i.test(trimmed.replace(/^pine:/, ''))
  const handle = normalizeUsername(trimmed)
  const canSearch = !looksLikeId && isValidUsername(handle)

  async function search () {
    setSearching(true)
    setResults(null)
    setSelected(null)
    try {
      const found = await store.client.findUsers(handle)
      setResults(found)
      if (found.length === 1) setSelected(found[0])
    } catch (err) {
      toast(err.message, { error: true })
    } finally {
      setSearching(false)
    }
  }

  async function start () {
    const account = selected ? selected.account : trimmed.replace(/^pine:/, '')
    setBusy(true)
    try {
      if (selected && selected.name) {
        await store.client.saveContact(account, { name: selected.name, username: selected.username })
      }
      // The chat opens straight away whether or not the other person is
      // awake; their invite is retried in the background until it lands.
      const { room } = await store.client.startDirectMessage(account, {
        name: selected ? selected.name : ''
      })
      onClose()
      await store.openRoom(room.id)
    } catch (err) {
      toast(errorText(err), { error: true })
    } finally {
      setBusy(false)
    }
  }

  const ready = !!selected || looksLikeId

  return html`
    <${Modal} title=${t('new.direct')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        ${canSearch && !ready
          ? html`
            <button class="btn btn-primary" disabled=${searching} onClick=${search}>
              ${searching ? html`<div class="spinner"></div>` : t('chats.search')}
            </button>`
          : html`
            <button class="btn btn-primary" disabled=${busy || !ready} onClick=${start}>
              ${busy ? html`<div class="spinner"></div>` : t('new.start')}
            </button>`}`}>

      <div class="field">
        <label>${t('search.byUsername')}</label>
        <input ref=${inputRef} class="input" value=${query} spellcheck="false" autocapitalize="off"
               placeholder="@alex"
               onInput=${(e) => { setQuery(e.target.value); setResults(null); setSelected(null) }}
               onKeyDown=${(e) => {
                 if (e.key !== 'Enter') return
                 if (canSearch && !ready) search()
                 else if (ready) start()
               }} />
        ${searching && html`<span class="hint">${t('search.searching')}</span>`}
      </div>

      ${results && results.length === 0 && html`
        <p class="dim small">${t('search.notFound')}</p>`}

      ${results && results.length > 1 && html`
        <p class="small" style="color:var(--warning)">${t('search.collision')}</p>`}

      ${results && results.length > 0 && html`
        <div style="margin:0 -8px">
          ${results.map((person) => html`
            <button key=${person.account}
                    class=${cx('list-row', selected && selected.account === person.account && 'selected')}
                    style="border-radius:var(--r-md)"
                    onClick=${() => setSelected(person)}>
              <${Avatar} name=${person.name} id=${person.account} src=${person.avatar} size="sm" />
              <span class="body">
                <span class="title">${person.name || formatUsername(person.username)}${Badges({ badges: person.badges })}</span>
                <span class="sub mono">${formatUsername(person.username)} · ${shortId(person.account)}</span>
              </span>
              ${selected && selected.account === person.account && html`
                <span class="glyph" style="color:var(--accent)">${Icon.check(17)}</span>`}
            </button>`)}
        </div>`}

      ${looksLikeId && html`
        <p class="hint mute small">${shortId(trimmed)}</p>`}

      <p class="hint mute small">${t('search.hint')}</p>
    <//>`
}

/**
 * Put a bot — or anybody — into one of my groups: the groups and channels I
 * may invite to and they are not in yet, one tap each.
 */
function AddToGroupDialog ({ account, name, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [groups, setGroups] = useState(null)
  const [busy, setBusy] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      const out = []
      for (const record of store.client.listRooms()) {
        if (record.placeholder || record.archived) continue
        if (record.type !== ROOM_TYPE.GROUP && record.type !== ROOM_TYPE.CHANNEL) continue
        const room = await store.client.openRoom(record.id).catch(() => null)
        if (!room) continue
        if (await room.view.isMember(account).catch(() => false)) continue
        if (!(await room.can(ACTION.INVITE).catch(() => false))) continue
        out.push(record)
      }
      if (alive) setGroups(out)
    })()
    return () => { alive = false }
  }, [store, account])

  async function add (record) {
    setBusy(record.id)
    try {
      await store.client.inviteToRoom(record.id, account)
      toast(t('bot.added', { name: record.name || '' }))
      onClose()
    } catch (err) {
      toast(err.reason ? errorText(err) : t('bot.addFailed'), { error: true })
    } finally {
      setBusy('')
    }
  }

  return html`
    <${Modal} title=${t('bot.addTitle', { name: name || '' })} onClose=${onClose}>
      ${groups === null
        ? html`<div class="row center" style="padding:24px"><div class="spinner lg"></div></div>`
        : groups.length === 0
          ? html`<${Empty} icon=${Icon.users ? Icon.users(34) : Icon.chat(34)} message=${t('bot.noGroups')} />`
          : html`
            <div class="col" style="margin:0 -8px">
              ${groups.map((record) => html`
                <div key=${record.id} class="list-row">
                  <${Avatar} name=${record.name} id=${record.id} src=${record.avatar} square=${record.type === ROOM_TYPE.CHANNEL} />
                  <span class="grow truncate">${record.name || shortId(record.id)}</span>
                  <button class="btn btn-primary btn-sm" disabled=${!!busy} onClick=${() => add(record)}>
                    ${busy === record.id ? html`<div class="spinner"></div>` : t('bot.add')}
                  </button>
                </div>`)}
            </div>`}
    <//>`
}

function InviteDialog ({ roomId, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [link, setLink] = useState('')
  const [busy, setBusy] = useState(true)

  useEffect(() => {
    let alive = true
    store.client.createInvite(roomId, {})
      .then((invite) => { if (alive) { setLink(invite.link()); setBusy(false) } })
      .catch((err) => { if (alive) { toast(err.message, { error: true }); setBusy(false) } })
    return () => { alive = false }
  }, [roomId])

  return html`
    <${Modal} title=${t('panel.invite')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-primary" disabled=${!link} onClick=${async () => {
          if (navigator.share) {
            try { await navigator.share({ title: 'Pine', url: link }); onClose(); return } catch {}
          }
          await copyText(link)
          toast(t('action.copied'))
          onClose()
        }}>${Icon.share ? '' : ''}${t('panel.copyLink')}</button>`}>
      ${busy
        ? html`<div class="row center" style="padding:24px"><div class="spinner lg"></div></div>`
        : html`
          <div class="col gap3">
            <p class="dim small">Ссылка действует, пока кто-то из участников онлайн.</p>
            <${CopyField} value=${link} />
          </div>`}
    <//>`
}

function SearchDialog ({ roomId, onClose }) {
  const store = useApp()
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState([])
  const inputRef = useRef(null)

  useEffect(() => { if (inputRef.current) inputRef.current.focus() }, [])

  useEffect(() => {
    let alive = true
    if (query.trim().length < 2) { setHits([]); return }
    const id = setTimeout(async () => {
      try {
        const room = await store.client.openRoom(roomId)
        const results = await room.view.search(query)
        if (alive) setHits(results)
      } catch {
        // The chat closed while searching: nothing to show.
        if (alive) setHits([])
      }
    }, 220)
    return () => { alive = false; clearTimeout(id) }
  }, [query, roomId, store])

  return html`
    <${Modal} title=${t('chats.search')} onClose=${onClose} wide>
      <label class="search">
        ${Icon.search(17)}
        <input ref=${inputRef} value=${query} placeholder=${t('chats.search')}
               onInput=${(e) => setQuery(e.target.value)} />
      </label>
      <div style="max-height:50vh;overflow-y:auto;margin:0 -8px">
        ${hits.length === 0
          ? html`<${Empty} message=${query.length >= 2 ? t('chats.empty') : ''} />`
          : hits.map((message) => html`
            <button key=${message.id} class="list-row" style="border-radius:var(--r-md)"
                    onClick=${() => {
                      onClose()
                      setTimeout(() => {
                        const node = document.querySelector('[data-id="' + message.id + '"]')
                        if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' })
                      }, 60)
                    }}>
              <span class="body">
                <span class="title clamp2" style="text-align:left">${message.text}</span>
                <span class="sub">${listTime(message.timestamp)}</span>
              </span>
            </button>`)}
      </div>
    <//>`
}

function ForwardDialog ({ dialog, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const rooms = store.client.listRooms().filter((r) => r.id !== dialog.roomId)

  async function forward (targetId) {
    setBusy(true)
    try {
      const source = await store.client.openRoom(dialog.roomId)
      const target = await store.client.openRoom(targetId)
      const message = await source.view.message(dialog.messageId)
      if (!message) throw new Error(t('err.generic'))

      const member = await source.view.member(message.author)
      await target.send({
        kind: message.kind,
        text: message.text,
        entities: message.entities,
        attachments: (message.attachments || []).map(rehydrate),
        forwardFrom: message.author,
        forwardName: (member && member.name) || shortId(message.author)
      })
      onClose()
      await store.openRoom(targetId)
    } catch (err) {
      toast(err.message, { error: true })
    } finally {
      setBusy(false)
    }
  }

  return html`
    <${Modal} title=${t('conv.forward')} onClose=${onClose}>
      <div style="max-height:52vh;overflow-y:auto;margin:0 -8px">
        ${rooms.map((room) => html`
          <button key=${room.id} class="list-row" style="border-radius:var(--r-md)"
                  disabled=${busy} onClick=${() => forward(room.id)}>
            <${Avatar} name=${room.name} id=${room.id} size="sm" />
            <span class="body"><span class="title">${room.name || shortId(room.id)}</span></span>
          </button>`)}
      </div>
    <//>`
}

/** Attachments come back from the view as JSON; the wire wants bytes again. */
function rehydrate (attachment) {
  return {
    ...attachment,
    blobId: base64ToBytes(attachment.blobId),
    core: attachment.core ? hexToBytes(attachment.core) : null,
    key: attachment.key ? hexToBytes(attachment.key) : null,
    thumb: attachment.thumb ? base64ToBytes(attachment.thumb) : null,
    waveform: attachment.waveform ? Uint8Array.from(attachment.waveform) : null
  }
}

function base64ToBytes (value) {
  const binary = atob(value)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

function hexToBytes (hex) {
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16)
  return out
}

function LeaveDialog ({ roomId, onClose }) {
  const store = useApp()
  return html`
    <${ConfirmDialog}
      title=${t('panel.leave')}
      message=${t('panel.leaveConfirm')}
      confirmLabel=${t('panel.leave')}
      danger
      onCancel=${onClose}
      onConfirm=${async () => {
        onClose()
        store.closeRoom()
        await store.client.deleteRoom(roomId, { leave: true }).catch(() => {})
      }} />`
}

/**
 * Reporting something to whoever runs the server.
 *
 * The screen's first job is to be honest about what leaves the device. Pine
 * spends every other screen telling people that their conversations cannot be
 * read by anyone but the people in them; this is the one place that stops
 * being true, and only because the person pressing the button decided it
 * should. So the warning is not fine print at the bottom — it is the sentence
 * above the buttons, it names what is being sent, and it never says "securely"
 * about a plain-text upload.
 *
 * Nothing is sent until the last press. Choosing a reason sends nothing;
 * opening this dialog sends nothing.
 */
function ReportDialog ({ dialog, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [reason, setReason] = useState('spam')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const aboutUser = dialog.kind === 'user'
  const sends = aboutUser ? t('report.sendsChat') : t('report.sendsMessage')

  async function send () {
    setBusy(true)
    try {
      const ok = aboutUser
        ? await store.client.reportUser({
            account: dialog.account, roomId: dialog.roomId || '', reason, note
          })
        : await store.client.reportMessage({
            roomId: dialog.roomId, messageId: dialog.messageId, reason, note
          })

      // A report that did not arrive must not look like one that did: somebody
      // who believes they have reported harassment and has not is worse off
      // than somebody who knows to try again.
      if (!ok) throw new Error(t('report.failed'))
      onClose()
      toast(t('report.sent'))
    } catch (err) {
      toast(err.message || t('report.failed'), { error: true })
      setBusy(false)
    }
  }

  return html`
    <${Modal} title=${t('report.title')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        <button class="btn btn-danger" disabled=${busy} onClick=${send}>
          ${busy ? html`<div class="spinner"></div>` : t('report.send')}
        </button>`}>

      <div class="field">
        <label>${t('report.reason')}</label>
        <div class="col gap1">
          ${REPORT_REASONS.map((id) => html`
            <button key=${id} class=${cx('list-row', reason === id && 'selected')}
                    style="border-radius:var(--r-md)"
                    onClick=${() => setReason(id)}>
              <span class="body"><span class="title">${t('report.reason.' + id)}</span></span>
              ${reason === id && html`<span class="glyph" style="color:var(--accent)">${Icon.check(17)}</span>`}
            </button>`)}
        </div>
      </div>

      <div class="field">
        <label>${t('report.note')}</label>
        <textarea class="textarea" rows="3" maxlength="1000" value=${note}
                  placeholder=${t('report.notePlaceholder')}
                  onInput=${(e) => setNote(e.target.value)}></textarea>
      </div>

      <p class="report-warning">${Icon.info(16)}<span>${sends}</span></p>
    <//>`
}

function DeleteMessageDialog ({ dialog, onClose }) {
  const store = useApp()
  const toast = useToast()
  return html`
    <${ConfirmDialog}
      title=${t('conv.delete')}
      message=${t('conv.deleteConfirm')}
      confirmLabel=${t('conv.deleteForAll')}
      danger
      onCancel=${onClose}
      onConfirm=${async () => {
        onClose()
        try {
          const room = await store.client.openRoom(dialog.roomId)
          await room.remove(dialog.messageId, { forEveryone: true })
        } catch (err) {
          toast(err.message, { error: true })
        }
      }} />`
}

export { cx }

/**
 * Signing out erases the account on this device.
 *
 * There is no server holding it, so there is nothing to log back into: the
 * recovery key is the only way back. The key is shown one last time here,
 * because a person who has not saved it is about to lose the account for good.
 */
function SignOutDialog ({ onClose }) {
  const store = useApp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  async function signOut () {
    setBusy(true)
    try {
      await store.client.signOut()
      // Nothing is left to render against; a fresh start lands on onboarding.
      // On the desktop that start has to be a new process: a reload keeps the
      // store's files locked by the page that is going away, and the reloaded
      // one could not open them — signing out ended on "Pine could not start".
      if (store.platform && typeof store.platform.relaunch === 'function') store.platform.relaunch()
      else if (typeof location !== 'undefined' && location.reload) location.reload()
      else onClose()
    } catch (err) {
      toast(err.message, { error: true })
      setBusy(false)
    }
  }

  return html`
    <${Modal} title=${t('settings.signOut')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        <button class="btn btn-danger" disabled=${busy || !copied} onClick=${signOut}>
          ${busy ? html`<div class="spinner"></div>` : t('settings.signOutConfirm')}
        </button>`}>
      <p>${t('settings.signOutWarning')}</p>
      <div class="recovery-key selectable" style="margin:10px 0">${store.client.identity.recoveryKey}</div>
      <button class="btn btn-soft" onClick=${async () => {
        await copyText(store.client.identity.recoveryKey)
        setCopied(true)
        toast(t('action.copied'))
      }}>${Icon.copy(16)} ${t('settings.signOutCopyKey')}</button>
      ${!copied && html`<p class="hint mute small" style="margin-top:8px">${t('settings.signOutNeedKey')}</p>`}
    <//>`
}

/* ------------------------------------------------------------------ */

export const DEVELOPER_EMAIL = 'mr_b4bin@tmail.ae'

/**
 * Settings → "Write to the developer": the address, to copy or to open in a
 * mail app. A plain address rather than a form: nothing here goes through
 * Pine's server, and the person keeps a copy of what they sent.
 */
function ContactDevDialog ({ dialog = {}, onClose }) {
  const store = useApp()
  const toast = useToast()
  // The Pine ID goes along: it is what the developer needs to open access
  // to Anya, and nobody has to find and copy it first. A dialog opened for
  // something in particular (Pine AI Pro) says so in the subject.
  const subject = dialog.subject || 'Pine'
  const href = 'mailto:' + DEVELOPER_EMAIL + '?subject=' + encodeURIComponent(subject) +
    '&body=' + encodeURIComponent((dialog.body || '') + '\n\nPine ID: ' + store.client.identity.id)

  return html`
    <${Modal} title=${dialog.title || t('settings.contactDev')} onClose=${onClose}
      footer=${html`<button class="btn btn-ghost" onClick=${onClose}>${t('action.close')}</button>`}>
      <p>${dialog.lede || t('contactDev.lede')}</p>
      <div class="recovery-key selectable" style="margin:12px 0;text-align:center;font-size:17px">${DEVELOPER_EMAIL}</div>
      <div class="row gap2" style="flex-wrap:wrap">
        <button class="btn btn-soft" onClick=${async () => {
          const ok = await copyText(DEVELOPER_EMAIL)
          toast(ok ? t('action.copied') : t('err.generic'), { error: !ok })
        }}>${Icon.copy(16)} ${t('contactDev.copy')}</button>
        <a class="btn btn-primary" href=${href} onClick=${(e) => {
          // The apps open links through their own bridge; a page just
          // follows the link to the mail app.
          if (store.platform && store.platform.openExternal) {
            e.preventDefault()
            store.platform.openExternal(href)
          }
        }}>${Icon.mail(16)} ${t('contactDev.write')}</a>
      </div>
    <//>`
}

/* ------------------------------------------------------------------ */

const POLL_MAX_OPTIONS = 10
const POLL_QUESTION_MAX = 255
const POLL_OPTION_MAX = 100

/**
 * Make a poll.
 *
 * Modelled on the one everybody already knows: a question, a list of answers
 * that grows a fresh empty row as soon as the last one is used, and two
 * switches underneath. Nothing is numbered "option 1..4" up front, because a
 * fixed set of boxes is a worse question than an empty one — most polls have
 * two or three answers and the rest is noise.
 *
 * Quiz mode is deliberately absent. It needs a right answer stored with the
 * poll and shown after voting, which is a change to what a poll *is* on the
 * wire, and this is not the place to make one quietly.
 */
function PollDialog ({ roomId, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [anonymous, setAnonymous] = useState(true)
  const [multiple, setMultiple] = useState(false)
  const [busy, setBusy] = useState(false)
  const lastOption = useRef(null)
  const focusLast = useRef(false)

  // Keep exactly one empty row at the end, up to the limit, so there is always
  // somewhere to type and never a column of blanks.
  useEffect(() => {
    const filled = options.filter((o) => o.trim().length > 0).length
    const last = options[options.length - 1]
    if (last !== undefined && last.trim() && options.length < POLL_MAX_OPTIONS) {
      setOptions((list) => [...list, ''])
    } else if (filled >= 2 && options.length > filled + 1) {
      // Two trailing blanks after clearing one; drop back to a single spare.
      setOptions((list) => list.slice(0, filled + 1))
    }
  }, [options])

  useEffect(() => {
    if (focusLast.current && lastOption.current) {
      lastOption.current.focus()
      focusLast.current = false
    }
  })

  const answers = options.map((o) => o.trim()).filter(Boolean)
  const ready = question.trim().length > 0 && answers.length >= 2

  function setOption (index, value) {
    setOptions((list) => list.map((item, i) => (i === index ? value : item)))
  }

  function removeOption (index) {
    setOptions((list) => {
      const next = list.filter((_, i) => i !== index)
      return next.length >= 2 ? next : [...next, '']
    })
  }

  async function create () {
    if (!ready || busy) return
    setBusy(true)
    try {
      const room = await store.client.openRoom(roomId)
      await room.createPoll({
        question: question.trim().slice(0, POLL_QUESTION_MAX),
        options: answers.map((o) => o.slice(0, POLL_OPTION_MAX)),
        multiple,
        anonymous
      })
      onClose()
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
      setBusy(false)
    }
  }

  return html`
    <${Modal} title=${t('poll.title')} onClose=${onClose}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onClose}>${t('action.cancel')}</button>
        <button class="btn btn-primary" disabled=${!ready || busy} onClick=${create}>
          ${busy ? html`<div class="spinner"></div>` : t('poll.create')}
        </button>`}>

      <div class="field">
        <label>${t('poll.question')}</label>
        <textarea class="input" rows="2" autofocus maxlength=${POLL_QUESTION_MAX}
                  placeholder=${t('poll.questionPlaceholder')}
                  value=${question}
                  onInput=${(e) => setQuestion(e.target.value)}></textarea>
      </div>

      <div class="section-title" style="padding-left:0">${t('poll.options')}</div>
      <div class="poll-options">
        ${options.map((value, index) => html`
          <div key=${index} class="poll-option-row">
            <span class="bullet"></span>
            <input class="input" maxlength=${POLL_OPTION_MAX}
                   ref=${index === options.length - 1 ? lastOption : null}
                   placeholder=${index < 2 ? t('poll.optionPlaceholder') : t('poll.optionMore')}
                   value=${value}
                   onInput=${(e) => setOption(index, e.target.value)}
                   onKeyDown=${(e) => {
                     if (e.key === 'Enter') {
                       e.preventDefault()
                       focusLast.current = true
                     }
                     if (e.key === 'Backspace' && !value && options.length > 2) {
                       e.preventDefault()
                       removeOption(index)
                     }
                   }} />
            ${(value || options.length > 2) && html`
              <button class="icon-btn" aria-label=${t('action.close')}
                      onClick=${() => removeOption(index)}>${Icon.close(15)}</button>`}
          </div>`)}
      </div>
      <p class="hint mute small">
        ${options.length >= POLL_MAX_OPTIONS ? t('poll.limit') : t('poll.optionsHint')}
      </p>

      <div class="divider"></div>
      <${SettingRow} icon=${Icon.eyeOff(18)} title=${t('poll.anonymous')}
        right=${html`<${Switch} checked=${anonymous} onChange=${setAnonymous} />`} />
      <${SettingRow} icon=${Icon.checkDouble(18)} title=${t('poll.multiple')}
        right=${html`<${Switch} checked=${multiple} onChange=${setMultiple} />`} />
    <//>`
}
